import React, { useEffect, useRef } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Alert, Button, Radio, Tooltip, Typography } from 'antd';
import {
    ITAR_CAPACITY_MESSAGE,
    ITAR_CHANGE_ANSWER,
    ITAR_NO_CONFIRMATION,
    ITAR_NO_TITLE,
    ITAR_NOTICE_TITLE,
    ITAR_QUESTION,
    ITAR_REQUIRED_ERROR,
    ITAR_TOOLTIP_EXPLANATION,
    ITAR_YES_EXPLANATION,
    ITAR_YES_TITLE,
} from '../itarCopy';

const { Text } = Typography;

export type ITARUploadAnswer = boolean | null;

interface ITARUploadRequirementProps {
    value: ITARUploadAnswer;
    onChange: (value: ITARUploadAnswer) => void;
    showRequiredError?: boolean;
    className?: string;
    focusSignal?: number;
    yesExplanation?: string;
}

export { ITAR_CAPACITY_MESSAGE };

export const ITARUploadRequirement: React.FC<ITARUploadRequirementProps> = ({
    value,
    onChange,
    showRequiredError = false,
    className = '',
    focusSignal = 0,
    yesExplanation,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!focusSignal) return;

        const firstOption = containerRef.current?.querySelector<HTMLInputElement>('input[type="radio"]');
        firstOption?.focus();
    }, [focusSignal]);

    const changeAnswerButton = (
        <Button type="link" className="p-0 mt-2" onClick={() => onChange(null)}>
            {ITAR_CHANGE_ANSWER}
        </Button>
    );

    if (value === false) {
        return (
            <Alert
                data-testid="itar-upload-requirement"
                className={className}
                type="success"
                showIcon
                message={ITAR_NO_TITLE}
                description={
                    <>
                        <div>{ITAR_NO_CONFIRMATION}</div>
                        {changeAnswerButton}
                    </>
                }
            />
        );
    }

    if (value === true) {
        return (
            <Alert
                data-testid="itar-upload-requirement"
                className={className}
                type="warning"
                showIcon
                message={ITAR_YES_TITLE}
                description={
                    <>
                        <div>{yesExplanation ?? ITAR_YES_EXPLANATION}</div>
                        {changeAnswerButton}
                    </>
                }
            />
        );
    }

    return (
        <Alert
            data-testid="itar-upload-requirement"
            className={className}
            type="warning"
            showIcon
            message={
                <span className="inline-flex items-center gap-1">
                    {ITAR_NOTICE_TITLE}
                    <Tooltip title={ITAR_TOOLTIP_EXPLANATION}>
                        <span className="inline-flex" aria-label="What is ITAR?">
                            <InfoCircleOutlined className="text-slate-500 hover:text-slate-700" />
                        </span>
                    </Tooltip>
                </span>
            }
            description={
                <div ref={containerRef}>
                    <p className="mb-2">{ITAR_QUESTION}</p>
                    <Radio.Group
                        value={value}
                        onChange={event => onChange(event.target.value)}
                        aria-label="Do your files contain ITAR-controlled or export-controlled content?"
                    >
                        <Radio value={true} data-testid="quote-itar-answer-yes">
                            Yes
                        </Radio>
                        <Radio value={false} data-testid="quote-itar-answer-no">
                            No
                        </Radio>
                    </Radio.Group>
                    {showRequiredError && value === null ? (
                        <Text type="danger" className="mt-2 block">
                            {ITAR_REQUIRED_ERROR}
                        </Text>
                    ) : null}
                </div>
            }
        />
    );
};
